import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EscrowPartyTypeEnum, AgreementFormEnum } from 'src/graphql/schema';

import { RepresentativeActions, SignatoryAction, SignatoryTooltip } from './';

import { useRepresentative, type IRepresentative } from '../model';

const columnHelper = createColumnHelper<IRepresentative>();

const baseColumns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (props) => <div className="text-sm">{props.getValue()}</div>,
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: (props) => <div className="text-sm">{props.getValue()}</div>,
  }),
  columnHelper.accessor('phone', {
    header: 'Phone number',
    cell: (props) => <div className="text-sm">{props.getValue() || '-'}</div>,
  }),
];

const RepresentativesTable = () => {
  const { representatives, role, currentPolicyRole, agreementType } = useRepresentative();
  const isSignatoryMissing = !representatives
    .filter(({ suggestType }) => suggestType !== 'remove')
    .some(({ signatory }) => signatory);

  // Add signatory column if needed
  const signatoryColumn =
    agreementType === AgreementFormEnum.Tripartite ||
    (agreementType === AgreementFormEnum.Bipartite && role === EscrowPartyTypeEnum.Depositor)
      ? [
          columnHelper.accessor('signatory', {
            header: () => (
              <div className="flex items-center gap-1.5">
                Signatory
                <SignatoryTooltip isSignatoryMissing={isSignatoryMissing} />
              </div>
            ),
            cell: (props) => <SignatoryAction {...props.row.original} />,
            meta: {
              headerClassName: 'w-30',
              cellClassName: 'w-30',
            },
          }),
        ]
      : [];

  // Add actions column if user has update permission
  const actionColumn = currentPolicyRole?.update
    ? [
        columnHelper.accessor('id', {
          header: '',
          cell: (props) => <RepresentativeActions id={props.getValue()} suggestType={props.row.original.suggestType} />,
          meta: {
            headerClassName: 'w-24.25',
            cellClassName: 'w-24.25',
          },
        }),
      ]
    : [];

  const columns = [...baseColumns, ...signatoryColumn, ...actionColumn];

  const table = useReactTable({
    data: representatives,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="rounded-md border border-border grow overflow-hidden h-full">
      <Table maxHeight="h-full">
        <TableHeader className="sticky top-0 bg-white z-10 after:absolute after:left-0 after:bottom-0 after:w-full after:border-b after:border-border">
          <TableRow>
            {table.getFlatHeaders().map((header) => (
              <TableHead key={header.id} className={header.column.columnDef.meta?.headerClassName}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className={cell.column.columnDef.meta?.cellClassName}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default RepresentativesTable;
